import { createContext, useContext, useState } from "react";
import { Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PowerDeleteContext = createContext({ active: false, setActive: () => {}, refreshToken: 0, bumpRefresh: () => {} });

export function PowerDeleteProvider({ children }) {
  const [active, setActive] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const bumpRefresh = () => setRefreshToken(t => t + 1);
  return (
    <PowerDeleteContext.Provider value={{ active, setActive, refreshToken, bumpRefresh }}>
      {active && (
        <div data-testid="power-delete-banner"
          className="py-2.5 px-4 flex items-center justify-center flex-wrap gap-3 text-sm font-medium shadow-md"
          style={{ background: "#b91c1c", color: "#fff" }}>
          <span>⚠ Power Delete Mode — every click deletes instantly (undo within 24h in "Recently deleted").</span>
          <button data-testid="exit-power-delete-btn" onClick={() => setActive(false)}
            className="underline underline-offset-2 flex-shrink-0">
            Exit Power Delete
          </button>
        </div>
      )}
      <div style={active ? { background: "rgba(185,28,28,0.09)" } : undefined}>
        {children}
      </div>
    </PowerDeleteContext.Provider>
  );
}

export function usePowerDelete() {
  return useContext(PowerDeleteContext);
}

/** Drop this next to any admin list item. Renders nothing unless Power
 * Delete mode is active. No confirmation — deletes immediately on click,
 * per product decision (undo lives in the 24h "Recently deleted" panel). */
export function PowerDeleteButton({ collection, id, onDeleted, className = "" }) {
  const { active, bumpRefresh } = usePowerDelete();
  if (!active) return null;

  const del = async () => {
    try {
      await api.post("/admin/power-delete", { collection, id });
      toast.success("Deleted — restorable for 24h in \"Recently deleted\"");
      bumpRefresh();
      if (onDeleted) onDeleted();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <button data-testid={`power-delete-${collection}-${id}`} onClick={del} type="button"
      title="Power delete — permanent after 24h"
      className={`inline-flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 transition-transform hover:scale-110 ${className}`}
      style={{ background: "#b91c1c", color: "#fff" }}>
      <Trash2 className="w-3.5 h-3.5" />
    </button>
  );
}
